// src/components/settings/shared/dialogs/PetBreedDialog.tsx

import React, { useState, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import DialogBase from '../../../common/modal/DialogBase';
import {
  StyledTextField,
  StyledSwitch,
  DailogLabel,
  DialogLabelText,
} from '../../../forms/shared/FormElements';
import FieldHelperText from '../../../forms/shared/FieldHelperText';
import { ModalCancelButton, ModalSaveButton } from '../../../common/Buttons';
import CustomSelect from '../../../common/CustomSelect';
import CustomSelectMenuItem from '../../../common/CustomSelectMenuItem';
import { PetBreed, PetType, PetSize } from '../../../../services/api';

interface PetBreedFormData {
  breed: string;
  petTypeId: number;
  petSizeFk: number | undefined;
  active: boolean;
}

interface PetBreedDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: PetBreedFormData) => void;
  editing: PetBreed | null;
  petTypes: PetType[];
  petSizes: PetSize[];
}

const PetBreedDialog: React.FC<PetBreedDialogProps> = ({
  open,
  onClose,
  onSave,
  editing,
  petTypes,
  petSizes,
}) => {
  const [formData, setFormData] = useState<PetBreedFormData>({
    breed: '',
    petTypeId: 0,
    petSizeFk: undefined,
    active: true,
  });

  useEffect(() => {
    if (open) {
      setFormData(
        editing
          ? {
              breed: editing.breed,
              petTypeId: (editing as any).petType ?? 0,
              petSizeFk: editing.petSizeFk ?? undefined,
              active: editing.active ?? true,
            }
          : { breed: '', petTypeId: 0, petSizeFk: undefined, active: true }
      );
    }
  }, [open, editing]);

  const canSave = formData.breed.trim().length > 0 && formData.petTypeId > 0;
  const isSystem = !!editing?.isSystem;

  const filteredSizes = petSizes.filter(
    (ps) => !formData.petTypeId || ps.petTypeFk === formData.petTypeId
  );

  return (
    <DialogBase
      open={open}
      onClose={onClose}
      title={editing ? 'Edit Breed' : 'Add Breed'}
      headerIcon={editing ? <EditIcon /> : <AddIcon />}
      maxWidth="sm"
      draggable
      footer={
        <>
          <ModalCancelButton
            onClick={onClose}
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
          <ModalSaveButton
            onClick={() => onSave(formData)}
            disabled={!canSave}
            text="Save"
            sx={{
              minWidth: { xs: 'auto', sm: '160px' },
              '& .MuiButton-startIcon': { display: { xs: 'none', sm: 'flex' } },
            }}
          />
        </>
      }
    >
      <Box display="flex" flexDirection="column" gap={2} mt={2}>
        <DailogLabel>
          <DialogLabelText id="pet-breed-name-label">
            Breed Name:
          </DialogLabelText>
        </DailogLabel>
        <StyledTextField
          id="pet-breed-name-input"
          inputProps={{ 'aria-labelledby': 'pet-breed-name-label' }}
          value={formData.breed}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
            setFormData({ ...formData, breed: e.target.value })
          }
          required
          fullWidth
          disabled={isSystem}
        />
        <DailogLabel>
          <DialogLabelText>Pet Type:</DialogLabelText>
        </DailogLabel>
        <CustomSelect
          value={formData.petTypeId ? String(formData.petTypeId) : ''}
          onChange={(value) =>
            setFormData({ ...formData, petTypeId: Number(value) })
          }
          ariaLabel="Pet Type"
          required
          disabled={isSystem}
        >
          {petTypes.map((pt) => (
            <CustomSelectMenuItem key={pt.id} value={String(pt.id)}>
              {pt.type}
            </CustomSelectMenuItem>
          ))}
        </CustomSelect>
        <DailogLabel>
          <DialogLabelText>Size (optional):</DialogLabelText>
        </DailogLabel>
        <CustomSelect
          value={formData.petSizeFk ? String(formData.petSizeFk) : ''}
          onChange={(value) =>
            setFormData({
              ...formData,
              petSizeFk: value ? Number(value) : undefined,
            })
          }
          ariaLabel="Size"
          disabled={isSystem}
        >
          <CustomSelectMenuItem value="">None</CustomSelectMenuItem>
          {filteredSizes.map((ps) => (
            <CustomSelectMenuItem key={ps.id} value={String(ps.id)}>
              {ps.size}
            </CustomSelectMenuItem>
          ))}
        </CustomSelect>
        {isSystem && (
          <FieldHelperText helperText="Standard system item - name, pet type and size cannot be changed. You can still deactivate it below." />
        )}
        {editing && (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography sx={{ fontSize: '1.8rem', fontWeight: 600 }}>
              Active
            </Typography>
            <StyledSwitch
              checked={formData.active}
              onChange={(e) =>
                setFormData({ ...formData, active: e.target.checked })
              }
            />
          </Box>
        )}
      </Box>
    </DialogBase>
  );
};

export default PetBreedDialog;
